import { AxiosResponse } from "axios";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

import { LOADING } from "../../store/actions";
import instance from "../../store/axios";
import User from "../../models/user";
import { CustomersListApiResponse } from "../login/types";
import { useNavigate } from "react-router-dom";

class CustomerService {
  private dispatch = useDispatch();
  private navigate = useNavigate();

  public async getCustomersListData(
    page,
    searchWord,
    sortField,
    sortOrder,
    filters
  ): Promise<CustomersListApiResponse | undefined> {
    this.dispatch({ type: LOADING, payload: true });
    let query = `?page=${page}&pageSize=20&sortField=${
      sortField || "lastLoginAt"
    }`;

    if (searchWord) {
      query += `&search=${searchWord}`;
    }
    query += `&sortOrder=${sortOrder === "ascend" ? "asc" : "desc"}`;

    if (filters?.length && filters != "null") {
      query += `&status=${filters}`;
    }

    try {
      const response: AxiosResponse<CustomersListApiResponse> =
        await instance.get(
          `/${process.env.REACT_APP_ENDPOINT_VERSION}/user${query}`
        );
      return response.data;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/home/customers-list`);
      return undefined;
    } finally {
      this.dispatch({ type: LOADING, payload: false });
    }
  }

  public async updateCustomerData(customer: User): Promise<boolean> {
    this.dispatch({ type: LOADING, payload: true });

    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/user/${customer?.id}`,
        customer
      );
      toast.success("Customer data updated successfully");
      return true;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/home/customers-list`);
      return false;
    } finally {
      this.dispatch({ type: LOADING, payload: false });
    }
  }

  public async resetPassword(userId): Promise<boolean> {
    this.dispatch({ type: LOADING, payload: true });

    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/user/${userId}/unlock-account-access`,
        { userId }
      );
      toast.success("Password reset successfully");
      return true;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/home/customers-list`);
      return false;
    } finally {
      this.dispatch({ type: LOADING, payload: false });
    }
  }

  public async sendResetPasswordEmail(userId, email): Promise<boolean> {
    this.dispatch({ type: LOADING, payload: true });

    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/user/send-reset-password-email`,
        { userId, email }
      );
      toast.success("Password reset email sent successfully");
      return true;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/home/customers-list`);
      return false;
    } finally {
      this.dispatch({ type: LOADING, payload: false });
    }
  }

  public async forceUpdatePassword(userId): Promise<boolean> {
    this.dispatch({ type: LOADING, payload: true });

    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/user/${userId}/force-reset-password`,
        { userId }
      );
      return true;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/home/customers-list`);
      return false;
    } finally {
      this.dispatch({ type: LOADING, payload: false });
    }
  }

  public async createCustomer(values: any): Promise<any> {
    this.dispatch({ type: LOADING, payload: true });

    try {
      const response = await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/create/saudi`,
        values
      );
      const data = response?.data?.data;

      if (data?.alreadyExists) {
        return data;
      }

      this.navigate(
        `/home/new-kyc/${data?.id}/${data?.accountInfo?.[0]?.id}`
      );
      toast.success("Customer created successfully");
      return data;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/home/create-customer`);
      return null;
    } finally {
      this.dispatch({ type: LOADING, payload: false });
    }
  }

}

export default CustomerService;
